import React, { useEffect, useState } from "react";
import { Globe, Check, Pipette, ExternalLink, Copy, Star, QrCode, Printer } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { Button, Toast, Modal } from "@/ui";
import { QRCodeSVG } from "qrcode.react";
import { TEMPLATES, THEMES, sampleLogoColour, accentText } from "@/storefront/lib";
import { TEMPLATE_MAP } from "@/storefront/templates";
import "@/storefront/storefront.css";

const noop = () => {};

function Preview({ template, theme, mode, tint, products, logo, name }) {
  const Template = TEMPLATE_MAP[template] || TEMPLATE_MAP.counter;
  const style = tint ? { "--sf-accent": tint, "--sf-accent-fg": accentText(tint) } : undefined;
  return (
    <div className="sf-preview-frame" data-testid="storefront-preview">
      <div className="sf" data-sf-theme={theme} data-sf-mode={mode} style={{ ...style, minHeight: "auto", paddingBottom: 24, pointerEvents: "none" }}>
        <div className="sf-header" style={{ paddingTop: 16 }}>
          {logo ? <img className="sf-logo" src={logo} alt="" width="44" height="44" /> : null}
          <h1 className="sf-shopname">{name}</h1>
        </div>
        <div className="sf-wrap" style={{ paddingTop: 4 }}>
          <Template products={products.slice(0, template === "single" ? 7 : 6)} onOpen={noop} onAdd={noop} />
        </div>
      </div>
    </div>
  );
}

export default function StorefrontStudio() {
  const [msg, setMsg] = useState("");
  const note = (t) => { setMsg(t); setTimeout(() => setMsg(""), 2600); };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [template, setTemplate] = useState("counter");
  const [theme, setTheme] = useState("clean");
  const [mode, setMode] = useState("light");
  const [tint, setTint] = useState(null);
  const [sampling, setSampling] = useState(false);
  const [featured, setFeatured] = useState("");
  const [showQr, setShowQr] = useState(false);

  useEffect(() => {
    api.get("/storefront/settings").then(({ data }) => {
      setShop(data.shop); setProducts(data.products || []);
      const sf = data.shop.storefront || {};
      setTemplate(sf.template || "counter"); setTheme(sf.theme || "clean");
      setMode(sf.mode || "light"); setTint(sf.tint || null); setFeatured(sf.featuredProductId || "");
    }).catch((e) => note(friendlyError(e))).finally(() => setLoading(false));
  }, []);

  const sampleTint = async () => {
    if (!shop?.logo) { note("Add a logo in shop settings first."); return; }
    setSampling(true);
    try { setTint(await sampleLogoColour(shop.logo)); note("Sampled your logo colour — preview updated."); }
    catch { note("Could not read the logo colour. You can still pick a theme."); }
    finally { setSampling(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/storefront/settings", { template, theme, mode, tint, featuredProductId: featured || null });
      setShop((s) => ({ ...s, storefront: data.storefront }));
      note("Shop page saved. Shoppers see it now.");
    } catch (e) { note(friendlyError(e)); }
    finally { setSaving(false); }
  };

  const copyLink = () => { navigator.clipboard?.writeText(shop.publicUrl); note("Page link copied."); };

  const localPathUrl = () => `${window.location.origin}/s/${(shop && (shop.slug || shop.id)) || ""}`;
  const printQr = () => {
    const w = window.open("", "_blank", "width=480,height=640");
    if (!w) { note("Allow pop-ups to print the QR."); return; }
    const svg = document.getElementById("sf-qr-svg");
    const svgHtml = svg ? svg.outerHTML : "";
    w.document.write(`<html><head><title>${shop.name} — shop QR</title></head><body style="font-family:system-ui;text-align:center;padding:40px">
      <h2>${shop.name}</h2><p>Scan to shop with us</p>${svgHtml}<p style="color:#666;font-size:13px">${shop.publicUrl}</p>
      <script>window.onload=()=>{window.print();}</script></body></html>`);
    w.document.close();
  };

  if (loading) return <div className="page"><p className="muted">Loading your shop page…</p></div>;
  if (!shop) return <div className="page"><p className="err-text">We could not open your shop page settings.</p></div>;

  const localPath = `/s/${shop.slug || shop.id}`;

  return (
    <div className="page" data-testid="storefront-studio">
      <div className="row" style={{ gap: 10, alignItems: "center", marginBottom: 6 }}>
        <Globe size={22} strokeWidth={1.75} />
        <h1 className="t-h1">Shop page</h1>
      </div>
      <p className="muted" style={{ marginBottom: 18 }}>
        Pick a look. Your page is made from your product list — no building, no code.
      </p>

      <div className="studio-grid">
        <div className="stack" style={{ gap: 22 }}>
          <section>
            <h2 className="t-h2" style={{ marginBottom: 10 }}>Layout</h2>
            <div className="stack" style={{ gap: 10 }}>
              {TEMPLATES.map((t) => (
                <button key={t.key} data-testid={`tpl-${t.key}`} onClick={() => setTemplate(t.key)}
                  className={`pick-row${template === t.key ? " on" : ""}`}>
                  <span>
                    <strong>{t.name}</strong>
                    <span className="muted t-small" style={{ display: "block" }}>{t.blurb}</span>
                  </span>
                  {template === t.key ? <Check size={18} /> : null}
                </button>
              ))}
            </div>
          </section>

          <section>
            <h2 className="t-h2" style={{ marginBottom: 10 }}>Theme</h2>
            <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
              {THEMES.map((th) => (
                <button key={th.key} data-testid={`theme-${th.key}`} onClick={() => setTheme(th.key)}
                  className={`swatch-btn${theme === th.key ? " on" : ""}`}>
                  <span className="swatch-dot" style={{ background: th.dot }} />
                  {th.name}
                </button>
              ))}
            </div>
            <div className="row" style={{ gap: 8, marginTop: 14 }}>
              <button data-testid="mode-light" onClick={() => setMode("light")} className={`pill${mode === "light" ? " on" : ""}`}>Light</button>
              <button data-testid="mode-dark" onClick={() => setMode("dark")} className={`pill${mode === "dark" ? " on" : ""}`}>Dark</button>
            </div>
          </section>

          <section>
            <h2 className="t-h2" style={{ marginBottom: 10 }}>Logo colour</h2>
            <div className="row" style={{ gap: 10, alignItems: "center" }}>
              <Button variant="secondary" onClick={sampleTint} loading={sampling} data-testid="sample-tint">
                <Pipette size={16} /> Use my logo colour
              </Button>
              {tint ? (
                <span className="row" style={{ gap: 8, alignItems: "center" }}>
                  <span className="swatch-dot" style={{ background: tint, width: 22, height: 22 }} />
                  <code className="mono">{tint}</code>
                  <button className="link-btn" onClick={() => setTint(null)} data-testid="clear-tint">remove</button>
                </span>
              ) : <span className="muted t-small">Tints buttons to match your logo. Preview first.</span>}
            </div>
          </section>

          <section>
            <h2 className="t-h2" style={{ marginBottom: 10 }}>Featured item</h2>
            <p className="muted t-small" style={{ marginBottom: 8 }}>Leads the Single layout and sits first in the grid. Sold-out items always drop to the bottom.</p>
            <select className="select" data-testid="featured-select" value={featured}
              onChange={(e) => setFeatured(e.target.value)}>
              <option value="">First item (automatic)</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </section>

          <section>
            <h2 className="t-h2" style={{ marginBottom: 6 }}>Your page link</h2>
            <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <code className="mono" data-testid="public-url">{shop.publicUrl}</code>
              <button className="link-btn" onClick={copyLink} data-testid="copy-link"><Copy size={14} /> copy</button>
              <a className="link-btn" href={localPath} target="_blank" rel="noreferrer" data-testid="open-page"><ExternalLink size={14} /> open</a>
              <button className="link-btn" onClick={() => setShowQr(true)} data-testid="show-qr"><QrCode size={14} /> QR code</button>
            </div>
          </section>

          <Button variant="primary" onClick={save} loading={saving} data-testid="save-storefront">Save shop page</Button>
        </div>

        <div className="studio-preview">
          <div className="row-between" style={{ marginBottom: 8 }}>
            <span className="t-label">Live preview · your products</span>
          </div>
          <Preview template={template} theme={theme} mode={mode} tint={tint} products={products} logo={shop.logo} name={shop.name} />
        </div>
      </div>
      {msg ? <div className="toast-wrap"><Toast tone="good" data-testid="storefront-toast">{msg}</Toast></div> : null}
      <Modal open={showQr} title="Printable shop QR" onClose={() => setShowQr(false)}
        footer={<><Button onClick={() => setShowQr(false)}>Close</Button><Button variant="primary" onClick={printQr} data-testid="print-qr"><Printer size={16} /> Print</Button></>}>
        <div style={{ textAlign: "center" }} data-testid="qr-modal">
          <p className="muted t-small" style={{ marginBottom: 12 }}>Tape this on your counter or delivery box. Scanning opens your shop page.</p>
          <div style={{ display: "inline-block", background: "#fff", padding: 16, borderRadius: 12 }}>
            <QRCodeSVG id="sf-qr-svg" value={localPathUrl()} size={200} level="M" />
          </div>
          <p className="mono t-small" style={{ marginTop: 12 }}>{shop.publicUrl}</p>
        </div>
      </Modal>
    </div>
  );
}
